import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, { useMemo, useState } from 'react';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import {
  PostLayoutSize,
  usePostLayoutSize,
} from '../../utils/responsive/PostLayoutSizeProvider';
import sortTopicsByName from '../../utils/sortTopicsByName';
import PostSeriesInTopicList from '../series/PostSeriesInTopicList';
import * as styles from './PostTopicListWithBreadcrumbs.module.scss';
import PostTopicWithBreadcrumbs from './PostTopicWithBreadcrumbs';

const TOPIC_FRAGMENT = gql(`
  fragment PostTopicListWithBreadcrumbs_topic on TopicFields {
    id
    ...PostTopicWithBreadcrumbs_topic
  }
`);

const SERIES_FRAGMENT = gql(`
  fragment PostTopicListWithBreadcrumbs_postSeries on SeriesFields {
    id
    ...PostSeriesInTopicList_series
  }
`);

const SERIES_DETAILS_FRAGMENT = gql(`
  fragment PostTopicListWithBreadcrumbs_postSeriesDetails on SeriesFields {
    id
    ...PostSeriesInTopicList_seriesDetails
  }
`);

type TopicFragment = DocumentType<typeof TOPIC_FRAGMENT>;

type PostTopicWithBreadcrumbsProps = React.ComponentProps<
  typeof PostTopicWithBreadcrumbs
>;

type PostTopicListWithBreadcrumbsProps<Topic extends TopicFragment> = {
  postId: string;
  postTopics: Topic[];
  postSeries?: DocumentType<typeof SERIES_FRAGMENT> | null;
  postSeriesDetails?: DocumentType<typeof SERIES_DETAILS_FRAGMENT>;
} & (
  | { editing?: false }
  | {
      editing: true;
      getRemoveTopicDisabledMessage: (topic: Topic) => string | undefined;
      onRemove: Extract<
        PostTopicWithBreadcrumbsProps,
        { editing: true }
      >['onRemove'];
    }
);

const PostTopicListWithBreadcrumbs = <Topic extends TopicFragment>({
  postId,
  postTopics,
  postSeries,
  postSeriesDetails,
  ...props
}: PostTopicListWithBreadcrumbsProps<Topic>) => {
  const postLayoutSize = usePostLayoutSize();

  const [topicContainerEl, setTopicContainerEl] =
    useState<HTMLUListElement | null>(null);
  const sortedTopics = useMemo(
    () => sortTopicsByName(postTopics),
    [postTopics],
  );
  const isLarge = postLayoutSize !== PostLayoutSize.mobile;

  const itemsCount = postTopics.length + (postSeries ? 1 : 0);

  if (itemsCount === 0) {
    return null;
  }

  const maxItemsPerRow = (() => {
    if (postLayoutSize === PostLayoutSize.mobile) return 1;
    if (postLayoutSize === PostLayoutSize.tablet) return 2;
    return 3;
  })();

  const itemsPerRow = _.clamp(itemsCount, 1, maxItemsPerRow) as 1 | 2 | 3;

  return (
    <div className={styles.container}>
      <ul
        ref={setTopicContainerEl}
        className={classNames(styles.topicContainer, {
          [styles.isSmall]: !isLarge,
        })}
        aria-label="Post topics"
      >
        {!!postSeries && (
          <PostSeriesInTopicList
            postId={postId}
            series={postSeries}
            seriesDetails={postSeriesDetails}
            size={isLarge ? 'large' : 'regular'}
          />
        )}
        {sortedTopics.map((topic) => (
          <PostTopicWithBreadcrumbs
            key={topic.id}
            topic={topic}
            className={styles.topic}
            {...props}
            containerEl={topicContainerEl}
            itemsPerRow={itemsPerRow}
            size={isLarge ? 'large' : 'regular'}
            {...(props.editing
              ? {
                  editing: true,
                  removeDisabledMessage:
                    props.getRemoveTopicDisabledMessage(topic),
                  onRemove: props.onRemove,
                }
              : { editing: false })}
          />
        ))}
      </ul>
    </div>
  );
};

export default PostTopicListWithBreadcrumbs;
